import { useEffect } from "react";
import { cases } from "@/mocks/cases";
import { caseDetails } from "@/mocks/caseDetails";

interface CaseModalProps {
  caseId: string | null;
  onClose: () => void;
}

export default function CaseModal({ caseId, onClose }: CaseModalProps) {
  const activeCase = caseId ? cases.find((item) => item.id === caseId) ?? null : null;
  const detail = caseId ? caseDetails.find((item) => item.id === caseId) ?? null : null;

  useEffect(() => {
    if (!activeCase) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [activeCase, onClose]);

  if (!activeCase) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 md:p-6">
      <div
        className="absolute inset-0 cursor-pointer bg-foreground-950/75 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={`档案详情：${activeCase.title}`}
        className="relative z-10 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-background-200 bg-background-50"
      >
        <div className="relative">
          <div className="h-48 w-full overflow-hidden md:h-64">
            <img
              src={activeCase.cover}
              alt={`${activeCase.categoryName}档案配图：${activeCase.title}`}
              title={`${activeCase.title} · 曝光档案局`}
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" />

          <button
            type="button"
            onClick={onClose}
            aria-label="关闭档案详情"
            className="absolute right-3 top-3 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-lg text-foreground-900 transition-colors hover:bg-background-50"
          >
            <i className="ri-close-line" />
          </button>

          <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1 text-[11px] font-medium text-background-50">
              <i className="ri-price-tag-3-line" />
              {activeCase.categoryName}
            </span>
            <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-background-50 md:text-2xl">
              {activeCase.title}
            </h3>
          </div>
        </div>

        <div className="p-4 md:p-6">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-600">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              收录于 {activeCase.date}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {activeCase.views} 次围观
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-time-line" />
              {activeCase.duration}
            </span>
          </div>

          {detail ? (
            <blockquote className="mt-5 rounded-md border-l-4 border-primary-500 bg-primary-50 px-4 py-3 text-sm leading-relaxed text-foreground-800 md:text-base">
              {detail.hook}
            </blockquote>
          ) : null}

          {detail ? (
            <div className="mt-6">
              <h4 className="font-heading text-base font-bold text-foreground-950 md:text-lg">关键节点回放</h4>
              <ol className="mt-4 space-y-4">
                {detail.timeline.map((step) => (
                  <li key={step.label} className="flex gap-3">
                    <span className="mt-0.5 flex h-7 w-16 shrink-0 items-center justify-center rounded-md bg-accent-100 text-[11px] font-semibold text-accent-900">
                      {step.label}
                    </span>
                    <p className="text-sm leading-relaxed text-foreground-700">{step.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}

          {detail ? (
            <div className="mt-6 rounded-md border border-background-200 bg-background-100 p-4">
              <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-primary-700">
                <i className="ri-lightbulb-flash-line text-base" />
                档案结论
              </p>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">{detail.verdict}</p>
            </div>
          ) : null}

          <div className="mt-6 flex flex-col gap-3 border-t border-background-200 pt-5 sm:flex-row">
            <a
              href="#guide"
              onClick={onClose}
              className="inline-flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-700"
            >
              <i className="ri-shield-star-line text-base" />
              看篇避雷指南
            </a>
            <a
              href="#contact"
              onClick={onClose}
              className="inline-flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-megaphone-line text-base" />
              我也有类似经历
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}